import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Recycle, FileDown, Trash2, Wrench, Paperclip, Package, PenLine, KeyRound, MessageCircle, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadBlob } from "../lib/api";
import { uploadRitiroDocumenti } from "../components/RitiroDaRiparazione";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/constants";
import ReportCommercialistaDialog from "../components/ReportCommercialistaDialog";
import GoogleDriveCard from "../components/GoogleDriveCard";

const RITIRO_STATI = [
  { id: "ritirato", label: "Ritirato" },
  { id: "in_vendita", label: "In vendita" },
  { id: "venduto", label: "Venduto (fattura)" },
  { id: "pezzi_ricambio", label: "Pezzi di ricambio" },
  { id: "uso_interno", label: "Uso interno" },
];
const STATO_CLS = { ritirato: "bg-slate-50", in_vendita: "bg-sky-50 text-sky-800", venduto: "bg-emerald-50 text-emerald-800", pezzi_ricambio: "bg-orange-50 text-orange-800", uso_interno: "bg-violet-50 text-violet-800" };
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY = { store_id: "", client_id: "", nome: "", cognome: "", codice_fiscale: "", telefono: "",
                articolo: "", imei: "", prezzo_ritiro: "", numero_documento: "", n_allegati: 2, data_ritiro: "" };

export default function Ritiri() {
  const { user } = useAuth();
  const [ritiri, setRitiri] = useState([]);
  const [meta, setMeta] = useState({ stores: [] });
  const [filters, setFilters] = useState({ q: "", venditore_id: "all" });
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [nuoviDoc, setNuoviDoc] = useState([]);
  const [docCliente, setDocCliente] = useState([]);
  const [riusaIds, setRiusaIds] = useState([]);
  const [sostituisci, setSostituisci] = useState(true);
  useEffect(() => {
    setRiusaIds([]);
    if (!form.client_id) { setDocCliente([]); return; }
    api.get(`/clients/${form.client_id}/attachments`).then((r) => setDocCliente(r.data)).catch(() => setDocCliente([]));
  }, [form.client_id]);
  const [otpRow, setOtpRow] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const reinviaFirma = async (r) => {
    try { const res = await api.post(`/ritiri/${r.id}/firma-reinvia`); res.data.ok ? toast.success("Link e OTP reinviati via WhatsApp") : toast.error(res.data.error || "Invio non riuscito"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const confermaOtp = async () => {
    try { await api.post(`/ritiri/${otpRow.id}/firma-otp`, { codice: otpCode }); toast.success(`Bolla ${otpRow.numero} firmata con OTP`); setOtpRow(null); setOtpCode(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const canSeeAll = user.role === "admin" || user.can_view_all;

  const load = useCallback(() => {
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.venditore_id !== "all") params.venditore_id = filters.venditore_id;
    api.get("/ritiri", { params }).then((r) => setRitiri(r.data))
      .catch((e) => toast.error(apiError(e, "Impossibile caricare i ritiri")));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (search.trim().length < 2 || form.client_id) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api.get("/clients", { params: { q: search.trim() } })
        .then((r) => setResults(r.data.slice(0, 6)))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search, form.client_id]);

  const pickClient = (c) => {
    setForm((f) => ({ ...f, client_id: c.id, nome: c.nome || "", cognome: c.cognome || "",
                      codice_fiscale: c.codice_fiscale || "", telefono: c.telefono || f.telefono || "" }));
    setResults([]);
  };

  const openNew = () => {
    setForm({ ...EMPTY, store_id: meta.stores[0]?.id || "" });
    setSearch("");
    setResults([]);
    setFormOpen(true);
  };

  const cambiaStato = async (r, stato) => {
    let numero_fattura = r.numero_fattura || "";
    if (stato === "venduto") {
      numero_fattura = window.prompt("Numero fattura di vendita:", numero_fattura || "");
      if (numero_fattura === null) return;
    }
    try {
      await api.patch(`/ritiri/${r.id}/stato`, { stato, numero_fattura });
      toast.success("Stato ritiro aggiornato");
      load();
    } catch (e) {
      toast.error(apiError(e, "Aggiornamento stato fallito"));
    }
  };

  const clienteParam = new URLSearchParams(window.location.search).get("cliente");
  const negozioParam = new URLSearchParams(window.location.search).get("negozio");
  const venditaParam = new URLSearchParams(window.location.search).get("vendita");
  const apriParam = new URLSearchParams(window.location.search).get("apri");
  useEffect(() => {
    if (!apriParam) return;
    api.get(`/ritiri/${apriParam}`).then((r) => setFilters((f) => ({ ...f, q: r.data.numero || "" }))).catch(() => {});
  }, [apriParam]);
  useEffect(() => {
    if (!clienteParam || !meta.stores.length) return;
    api.get(`/clients/${clienteParam}`).then((r) => {
      const c = r.data;
      const storeOk = meta.stores.some((s) => s.id === negozioParam);
      setForm({ ...EMPTY, store_id: (storeOk && negozioParam) || c.venditore_id || meta.stores[0]?.id || "", client_id: c.id, nome: c.nome || "", cognome: c.cognome || "", codice_fiscale: c.codice_fiscale || "", telefono: c.telefono || "", vendita_id: venditaParam || "" });
      setFormOpen(true);
    }).catch(() => {});
  }, [clienteParam, negozioParam, venditaParam, meta.stores]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        prezzo_ritiro: form.prezzo_ritiro === "" ? null : parseFloat(form.prezzo_ritiro),
        n_allegati: parseInt(form.n_allegati) || 2,
        data_ritiro: form.data_ritiro || null,
      };
      const res = await api.post("/ritiri", payload);
      const esiti = [];
      if (nuoviDoc.length) {
        try { await uploadRitiroDocumenti(res.data.id, nuoviDoc, sostituisci); esiti.push(`${nuoviDoc.length} documenti nuovi`); }
        catch (e) { toast.error(apiError(e, "Bolla creata ma documenti non caricati: allegali dalla lista")); }
      }
      if (riusaIds.length) {
        try { await api.post(`/ritiri/${res.data.id}/documenti-da-cliente`, { attachment_ids: riusaIds }); esiti.push(`${riusaIds.length} documenti riusati dalla scheda cliente`); }
        catch (e) { toast.error(apiError(e, "Documenti del cliente non allegati")); }
      }
      toast.success(`Bolla ${res.data.numero} generata${esiti.length ? ` con ${esiti.join(" e ")}` : ""} · firma inviata via WhatsApp${form.vendita_id ? " · permuta scalata dalla vendita" : ""}`);
      setNuoviDoc([]);
      setRiusaIds([]);
      setFormOpen(false);
      if (form.vendita_id) { navigate(`/vendite?apri=${form.vendita_id}`); return; }
      load();
    } catch (err) {
      toast.error(apiError(err, "Salvataggio fallito"));
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/ritiri/${r.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${r.numero}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(apiError(e, "Download fallito"));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la bolla ${r.numero}?`)) return;
    try {
      await api.delete(`/ritiri/${r.id}`);
      toast.success("Ritiro eliminato");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const allegaDocumenti = (r) => {
    const input = document.createElement("input");
    input.type = "file"; input.multiple = true; input.accept = "application/pdf,image/jpeg,image/png,image/webp";
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;
      try {
        await uploadRitiroDocumenti(r.id, files);
        toast.success(`${files.length} documenti uniti alla bolla ${r.numero}`);
        load();
      } catch (e) { toast.error(apiError(e, "Caricamento documenti fallito")); }
    };
    input.click();
  };

  return (
    <div className="space-y-6" data-testid="ritiri-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Ritiri usato</h1>
          <p className="mt-1 text-sm text-slate-500">{ritiri.length} bolle di ritiro generate</p>
        </div>
        <div className="flex gap-2">
          {(user.role === "admin" || user.can_view_all) && <ReportCommercialistaDialog stores={meta.stores} />}
          {(user.role === "admin" || user.can_view_all) && (
            <Button variant="outline" className="gap-2" data-testid="ritiri-import-button" onClick={async () => {
              if (!window.confirm("Importare il registro ritiri 2026 dai fogli Google (Morbegno, Sondrio, Gravedona)? I ritiri già importati vengono saltati.")) return;
              try {
                const r = await api.post("/ritiri/import-sheet", { sheet_url: "https://docs.google.com/spreadsheets/d/1_j80xlW3jfMPwoULx50u0EfDMBlJiOVj0G3pjD_EMBw/edit",
                  gids: { "1195285320": "Morbegno", "1042530783": "Sondrio", "1560914735": "Gravedona" } });
                toast.success(`Import: ${r.data.importati} ritiri importati, ${r.data.saltati} già presenti`);
                load();
              } catch (e) { toast.error(apiError(e, "Import fallito")); }
            }}>
              <FileDown className="h-4 w-4" /> Importa registro 2026
            </Button>
          )}
          <Button variant="outline" onClick={() => downloadBlob("/ritiri/export", `ritiri_usato_${new Date().toISOString().slice(0, 10)}.xlsx`)} data-testid="ritiri-export-button" className="gap-2">
            <FileDown className="h-4 w-4" /> Excel
          </Button>
          <Button onClick={openNew} data-testid="add-ritiro-button" className="gap-2 bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4" /> Nuovo ritiro
          </Button>
        </div>
      </div>

      {(user.role === "admin" || user.can_view_all) && <GoogleDriveCard isAdmin={user.role === "admin"} />}

      <div className="flex flex-wrap gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="ritiri-filters">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca cliente, articolo o N° ritiro..." className="pl-9" value={filters.q}
                 onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="ritiri-search" />
        </div>
        {canSeeAll && (
          <Select value={filters.venditore_id} onValueChange={(v) => setFilters((f) => ({ ...f, venditore_id: v }))}>
            <SelectTrigger className="w-[200px]" data-testid="ritiri-negozio-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i negozi</SelectItem>
              {meta.stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ritiri-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">N° ritiro</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Articolo</th>
                <th className="px-4 py-3">Riparazione</th>
                <th className="px-4 py-3">Prezzo ritiro</th>
                <th className="px-4 py-3">Stato</th>
                {canSeeAll && <th className="px-4 py-3">Negozio</th>}
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ritiri.map((r, i) => (
                <tr key={r.id} className={`transition-colors hover:bg-slate-50 ${r.id === apriParam ? "bg-teal-50" : ""}`} data-testid={`ritiro-row-${i}`}>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 font-mono text-xs font-bold text-slate-800">
                      <Recycle className="h-3.5 w-3.5 text-emerald-600" /> {r.numero}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(r.data_ritiro)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.cognome} {r.nome}</td>
                  <td className="px-4 py-3 text-slate-600">{r.articolo}{r.imei ? ` · IMEI ${r.imei}` : ""}
                    <span className="mt-0.5 flex flex-wrap gap-1">
                      {(r.documenti || []).length > 0 && <span className="rounded bg-sky-50 px-1.5 text-[10px] font-semibold text-sky-700" data-testid={`ritiro-docs-badge-${i}`}><Paperclip className="inline h-3 w-3" /> {r.documenti.length} doc.</span>}
                      {r.drive_link && <a href={r.drive_link} target="_blank" rel="noopener noreferrer" className="rounded bg-emerald-50 px-1.5 text-[10px] font-semibold text-emerald-700 hover:underline" data-testid={`ritiro-drive-badge-${i}`}>Drive</a>}
                      {r.magazzino_id && <span className="rounded bg-violet-50 px-1.5 text-[10px] font-semibold text-violet-700" data-testid={`ritiro-rigenerato-badge-${i}`}><Package className="inline h-3 w-3" /> rigenerato</span>}
                      {r.firma_stato === "firmata"
                        ? <span className="rounded bg-emerald-50 px-1.5 text-[10px] font-semibold text-emerald-700" data-testid={`ritiro-firma-badge-${i}`}><PenLine className="inline h-3 w-3" /> firmata ({r.firma_metodo === "otp" ? "OTP" : "link"})</span>
                        : r.firma_token && <span className="rounded bg-amber-50 px-1.5 text-[10px] font-semibold text-amber-700" data-testid={`ritiro-firma-badge-${i}`}><PenLine className="inline h-3 w-3" /> firma in attesa</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {r.servizio_id
                      ? <Link to={`/riparazioni?apri=${r.servizio_id}`} className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-500/15 px-2 py-0.5 text-xs font-semibold text-sky-700 hover:bg-sky-500/25"
                              data-testid={`ritiro-riparazione-link-${i}`}>
                          <Wrench className="h-3 w-3" /> {r.riparazione_numero || "Scheda"}
                        </Link>
                      : <span className="text-xs text-slate-400">-</span>}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    {r.prezzo_ritiro != null ? `€ ${Number(r.prezzo_ritiro).toFixed(2)}` : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <Select value={r.stato || (r.magazzino_id ? "in_vendita" : "ritirato")} onValueChange={(v) => cambiaStato(r, v)}>
                      <SelectTrigger className={`h-7 w-[150px] text-xs ${STATO_CLS[r.stato || "in_vendita"] || ""}`} data-testid={`ritiro-stato-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RITIRO_STATI.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {r.stato === "venduto" && r.numero_fattura && <p className="mt-0.5 text-[10px] text-slate-500">Fatt. {r.numero_fattura}</p>}
                  </td>
                  {canSeeAll && <td className="px-4 py-3 text-slate-600">{r.store_name || "-"}</td>}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" title="Scarica bolla PDF" onClick={() => downloadPdf(r)} data-testid={`ritiro-pdf-${i}`}>
                        <FileDown className="h-4 w-4 text-sky-600" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Allega documenti alla bolla" onClick={() => allegaDocumenti(r)} data-testid={`ritiro-docs-${i}`}>
                        <Paperclip className="h-4 w-4 text-slate-500" />
                      </Button>
                      {r.firma_token && r.firma_stato !== "firmata" && (
                        <>
                          <Button variant="ghost" size="icon" title="Firma con codice OTP del cliente" onClick={() => setOtpRow(r)} data-testid={`ritiro-otp-${i}`}><KeyRound className="h-4 w-4 text-amber-600" /></Button>
                          <Button variant="ghost" size="icon" title="Reinvia link/OTP via WhatsApp" onClick={() => reinviaFirma(r)} data-testid={`ritiro-firma-reinvia-${i}`}><MessageCircle className="h-4 w-4 text-emerald-600" /></Button>
                        </>
                      )}
                      {user.role === "admin" && (
                        <Button variant="ghost" size="icon" onClick={() => remove(r)} data-testid={`ritiro-del-${i}`}>
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {ritiri.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-500" data-testid="ritiri-empty">
                  Nessun ritiro registrato. Crea la prima bolla con "Nuovo ritiro".
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="ritiro-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Nuova bolla di ritiro usato</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="ritiro-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Negozio *</Label>
              <Select value={form.store_id} onValueChange={(v) => setForm({ ...form, store_id: v })} disabled={user.role === "negozio"}>
                <SelectTrigger data-testid="ritiro-form-store"><SelectValue placeholder="Seleziona negozio" /></SelectTrigger>
                <SelectContent>
                  {meta.stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 rounded-xl border border-slate-200 p-4" data-testid="ritiro-client-picker">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cliente</Label>
              {form.client_id ? (
                <div className="flex items-center justify-between rounded-lg bg-sky-50 px-3 py-2">
                  <span className="text-sm font-medium text-sky-800" data-testid="ritiro-client-selected">{form.cognome} {form.nome}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, client_id: "" }))} data-testid="ritiro-client-clear">Cambia</Button>
                </div>
              ) : (
                <>
                  <Input placeholder="Cerca anagrafica per nome, telefono, CF..." value={search}
                         onChange={(e) => setSearch(e.target.value)} data-testid="ritiro-client-search" />
                  {results.length > 0 && (
                    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {results.map((c) => (
                        <button type="button" key={c.id}
                                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                                onClick={() => pickClient(c)} data-testid={`ritiro-client-option-${c.id}`}>
                          <span className="font-medium">{c.cognome} {c.nome}</span>
                          <span className="text-xs text-slate-500">{c.telefono}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Nome *" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="ritiro-form-nome" />
                <Input placeholder="Cognome *" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} data-testid="ritiro-form-cognome" />
              </div>
              <Input placeholder="Codice fiscale" value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} data-testid="ritiro-form-cf" />
              <Input placeholder="Telefono cliente * (per la firma via WhatsApp)" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="ritiro-form-telefono" />
              <div className="rounded-lg border border-dashed border-slate-300 p-3 text-sm" data-testid="ritiro-form-documenti">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-2 font-medium text-slate-700"><Paperclip className="h-4 w-4" /> Allega subito documenti (carta d'identità, ecc.) — salvati anche sulla scheda cliente
                    <input type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { setNuoviDoc([...nuoviDoc, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="ritiro-form-documenti-input" /></label>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-800 hover:bg-sky-100"><Camera className="h-4 w-4" /> Fotografa documento
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { setNuoviDoc([...nuoviDoc, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="ritiro-form-camera-input" /></label>
                </div>
                {nuoviDoc.length > 0 && <ul className="mt-2 space-y-1 text-xs text-slate-600">{nuoviDoc.map((f, i) => <li key={i} className="flex justify-between"><span>{f.name}</span><button type="button" className="text-rose-600" onClick={() => setNuoviDoc(nuoviDoc.filter((_, j) => j !== i))}>rimuovi</button></li>)}</ul>}
                {nuoviDoc.length > 0 && form.client_id && docCliente.length > 0 && (
                  <label className="mt-2 flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={sostituisci} onChange={(e) => setSostituisci(e.target.checked)} data-testid="ritiro-form-sostituisci" /> Sostituisci sulla scheda cliente i documenti dei ritiri precedenti (potrebbero essere scaduti)</label>
                )}
                {form.client_id && docCliente.length > 0 && (
                  <div className="mt-3 border-t border-dashed border-slate-200 pt-2" data-testid="ritiro-doc-cliente">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Documenti già sulla scheda cliente</p>
                    <ul className="mt-1 space-y-1 text-xs">
                      {docCliente.map((a) => {
                        const sel = riusaIds.includes(a.id);
                        const vecchio = (Date.now() - new Date(a.created_at).getTime()) > 365 * 24 * 3600 * 1000 * 5;
                        return (
                          <li key={a.id} className="flex items-center justify-between gap-2">
                            <span className="truncate text-slate-700">{a.original_filename} <span className="text-slate-400">· {new Date(a.created_at).toLocaleDateString("it-IT")}</span>{vecchio && <span className="ml-1 text-amber-600">(oltre 5 anni: verifica scadenza)</span>}</span>
                            <button type="button" className={`rounded px-2 py-0.5 font-medium ${sel ? "bg-emerald-600 text-white" : "border border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                                    onClick={() => setRiusaIds(sel ? riusaIds.filter((x) => x !== a.id) : [...riusaIds, a.id])} data-testid={`ritiro-riusa-${a.id}`}>{sel ? "Allegato ✓" : "Allega alla bolla"}</button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Articolo ritirato *</Label>
                <Input required placeholder="es. iPhone 12 Pro Max 128GB" value={form.articolo}
                       onChange={(e) => setForm({ ...form, articolo: e.target.value })} data-testid="ritiro-form-articolo" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">IMEI</Label>
                <Input value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value })} data-testid="ritiro-form-imei" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prezzo ritiro €{form.vendita_id ? " (scalato dalla vendita come permuta)" : ""}</Label>
                <Input type="number" step="0.01" value={form.prezzo_ritiro}
                       onChange={(e) => setForm({ ...form, prezzo_ritiro: e.target.value })} data-testid="ritiro-form-prezzo" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">N° documento (carta identità)</Label>
                <Input value={form.numero_documento} onChange={(e) => setForm({ ...form, numero_documento: e.target.value })} data-testid="ritiro-form-documento" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Documenti allegati (n.)</Label>
                <Input type="number" min="0" value={form.n_allegati}
                       onChange={(e) => setForm({ ...form, n_allegati: e.target.value })} data-testid="ritiro-form-allegati" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data ritiro</Label>
                <Input type="date" value={form.data_ritiro} onChange={(e) => setForm({ ...form, data_ritiro: e.target.value })} data-testid="ritiro-form-data" />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)} data-testid="ritiro-form-cancel">Annulla</Button>
              <Button type="submit" disabled={saving || !form.nome.trim() || !form.cognome.trim() || !form.articolo.trim() || !form.store_id || (form.telefono || "").replace(/\D/g, "").length < 9}
                      className="bg-slate-900 hover:bg-slate-800" data-testid="ritiro-form-submit">
                {saving ? "Generazione bolla..." : "Genera bolla PDF"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(otpRow)} onOpenChange={(o) => { if (!o) { setOtpRow(null); setOtpCode(""); } }}>
        <DialogContent className="max-w-sm" data-testid="otp-dialog">
          <DialogHeader><DialogTitle>Firma con codice OTP</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Chiedi al cliente il codice a 6 cifre ricevuto su WhatsApp per la bolla <b>{otpRow?.numero}</b> ({otpRow?.telefono}).</p>
          <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="000000" inputMode="numeric" maxLength={6} className="text-center text-2xl tracking-[0.5em]" data-testid="otp-input" />
          <div className="flex justify-between gap-2">
            <Button variant="ghost" onClick={() => otpRow && reinviaFirma(otpRow)} data-testid="otp-reinvia">Reinvia codice</Button>
            <Button onClick={confermaOtp} disabled={otpCode.replace(/\D/g, "").length !== 6} data-testid="otp-conferma">Conferma firma</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
